/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/**
 * Restores cart drawer scroll after innerHTML replaces section markup (Safari resets scroll).
 * @param {number} contentScroll - .drawer__content scrollTop
 * @param {number} drawerScroll - cart-drawer host scrollTop
 */
function restoreCartDrawerScroll(contentScroll, drawerScroll) {
  const cartDrawer = document.getElementById('cart-drawer');
  if (!cartDrawer) return;
  const apply = () => {
    const drawerContent = cartDrawer.querySelector('.drawer__content');
    if (drawerContent) drawerContent.scrollTop = contentScroll;
    cartDrawer.scrollTop = drawerScroll;
  };
  apply();
  requestAnimationFrame(() => {
    apply();
    requestAnimationFrame(apply);
  });
  setTimeout(apply, 0);
  setTimeout(apply, 50);
  setTimeout(apply, 100);
  setTimeout(apply, 350);
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.initBasePricesTimeout = null; // Track timeout for base price initialization
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      // Remove cart icon handlers
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon && this._cartIconClickHandler) {
        cartIcon.removeEventListener('click', this._cartIconClickHandler);
        this._cartIconClickHandler = null;
      }
      if (cartIcon && this._cartIconKeydownHandler) {
        cartIcon.removeEventListener('keydown', this._cartIconKeydownHandler);
        this._cartIconKeydownHandler = null;
      }

      // Clean up timeout
      if (this.initBasePricesTimeout) {
        clearTimeout(this.initBasePricesTimeout);
        this.initBasePricesTimeout = null;
      }

      // Reset initialization flag
      this._initialized = false;
    }

    init() {
      // Prevent duplicate initialization
      if (this._initialized) {
        return;
      }
      this._initialized = true;

      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        // Remove old handlers if they exist (in case of reconnection)
        if (this._cartIconClickHandler) {
          cartIcon.removeEventListener('click', this._cartIconClickHandler);
        }
        if (this._cartIconKeydownHandler) {
          cartIcon.removeEventListener('keydown', this._cartIconKeydownHandler);
        }

        // Create bound handlers
        this._cartIconClickHandler = (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        };
        this._cartIconKeydownHandler = (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        };

        cartIcon.addEventListener('click', this._cartIconClickHandler);
        cartIcon.addEventListener('keydown', this._cartIconKeydownHandler);
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      // Delegated events inside the cart drawer (survives section re-renders).
      this.drawerClickHandler = this.drawerClickHandler || this.handleDrawerClick.bind(this);
      this.addEventListener('click', this.drawerClickHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Handle delegated click events inside the cart drawer.
     * @param {MouseEvent} evt
     */
    handleDrawerClick(evt) {
      const clearCartBtn = evt.target.closest('.js-cart-clear-btn');
      if (clearCartBtn) {
        evt.preventDefault();
        const confirmed = window.confirm('Clear all items from your cart?');
        if (!confirmed) return;

        this.clearCart(clearCartBtn).catch((error) => {
          console.error('[CartDrawer] Failed to clear cart:', error);
        });
        return;
      }

      const clearAllBtn = evt.target.closest('.cart-drawer__clear-all-btn');
      if (clearAllBtn) {
        if (window.__isalbiDeliverAllLaterDate && typeof window.__isalbiDeliverAllLaterDate.clear === 'function') {
          window.__isalbiDeliverAllLaterDate.clear();
        }
        const cartItems = this.querySelector('cart-items');
        if (cartItems && typeof cartItems.clearAllDeliverLater === 'function') {
          cartItems.clearAllDeliverLater();
        } else {
          // Fallback: best-effort UI update if cart-items component isn't available.
          const toggles = this.querySelectorAll('.cart-item__deliver-later-toggle');
          toggles.forEach((toggle) => {
            const line = toggle.dataset.line;
            const datepicker = this.querySelector(`.cart-item__deliver-later-datepicker[data-line="${line}"]`);
            const dateInput = this.querySelector(`.cart-item__deliver-later-date[data-line="${line}"]`);
            const label = toggle.closest('.cart-item__deliver-later-btn');
            toggle.checked = false;
            if (label) label.classList.remove('is-checked');
            if (datepicker) datepicker.hidden = true;
            if (dateInput) dateInput.value = '';
          });
        }

        evt.preventDefault();
        return;
      }

      const applyDeliverAllLaterBtn = evt.target.closest('.cart-drawer__deliver-all-later-btn');
      if (applyDeliverAllLaterBtn) {
        const drawerDateInput = this.querySelector('#deliver-all-later-date');
        const forwardDeliveryDate = drawerDateInput ? drawerDateInput.value : null;

        if (forwardDeliveryDate && window.__isalbiDeliverAllLaterDate && typeof window.__isalbiDeliverAllLaterDate.set === 'function') {
          window.__isalbiDeliverAllLaterDate.set(forwardDeliveryDate);
        }

        const cartItems = this.querySelector('cart-items');
        if (cartItems && typeof cartItems.applyDeliverAllLaterDate === 'function') {
          cartItems.applyDeliverAllLaterDate(forwardDeliveryDate);
        } else if (forwardDeliveryDate) {
          // Fallback: best-effort UI update if cart-items component isn't available.
          const toggles = this.querySelectorAll('.cart-item__deliver-later-toggle');
          toggles.forEach((toggle) => {
            const line = toggle.dataset.line;
            const datepicker = this.querySelector(`.cart-item__deliver-later-datepicker[data-line="${line}"]`);
            const dateInput = this.querySelector(`.cart-item__deliver-later-date[data-line="${line}"]`);
            toggle.checked = true;
            if (datepicker) datepicker.hidden = false;
            if (dateInput) dateInput.value = forwardDeliveryDate;
          });
        }

        evt.preventDefault();
        return;
      }

      const deliverAllLaterBtn = evt.target.closest('.cart-drawer__deliver-all-later');
      if (!deliverAllLaterBtn) return;

      const datepicker = this.querySelector('#deliver-all-later-datepicker');
      if (!datepicker) return;

      // Toggle the datepicker.
      const isHidden = datepicker.hasAttribute('hidden') || datepicker.hidden === true;
      if (!isHidden) {
        datepicker.hidden = true;
        datepicker.setAttribute('hidden', '');
        deliverAllLaterBtn.setAttribute('aria-expanded', 'false');
        deliverAllLaterBtn.setAttribute('aria-controls', 'deliver-all-later-datepicker');
        deliverAllLaterBtn.focus({ preventScroll: true });

        if (this.cartSummary?.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        evt.preventDefault();
        return;
      }

      datepicker.hidden = false;
      datepicker.removeAttribute('hidden');
      deliverAllLaterBtn.setAttribute('aria-expanded', 'true');
      deliverAllLaterBtn.setAttribute('aria-controls', 'deliver-all-later-datepicker');

      // Ensure the date input reflects the persisted bulk date (if any).
      if (window.__isalbiDeliverAllLaterDate && typeof window.__isalbiDeliverAllLaterDate.sync === 'function') {
        window.__isalbiDeliverAllLaterDate.sync();
      }

      // Scroll the drawer content to the datepicker.
      window.requestAnimationFrame(() => {
        const scrollContainer = this.querySelector('.drawer__content');
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const targetRect = datepicker.getBoundingClientRect();
          const top = (targetRect.top - containerRect.top) + scrollContainer.scrollTop;
          scrollContainer.scrollTo({ top, behavior: 'smooth' });
        } else {
          // Fallback: scroll the nearest scrollable ancestor.
          datepicker.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // Keep sticky footer calculations correct when content height changes.
        if (this.cartSummary?.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        // Optional: move focus to the date input for better accessibility.
        const dateInput = datepicker.querySelector('input[type="date"], .cart-drawer__deliver-all-later-date');
        if (dateInput && typeof dateInput.focus === 'function') dateInput.focus({ preventScroll: true });
      });

      // Prevent any default action (in case markup changes to links later).
      evt.preventDefault();
    }

    async clearCart(triggerEl) {
      if (triggerEl) {
        triggerEl.disabled = true;
        triggerEl.classList.add('is-loading');
      }

      try {
        const response = await fetch('/cart/clear.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to clear cart (${response.status})`);
        }

        await this.refresh(true);
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const countNodes = cartIconBubble.querySelectorAll('.header__cart-count-number');
          countNodes.forEach((node) => {
            node.textContent = '0';
          });
          const countContainers = cartIconBubble.querySelectorAll('.header__cart-count');
          countContainers.forEach((node) => {
            node.style.display = 'none';
            node.setAttribute('aria-hidden', 'true');
          });
        }
        document.dispatchEvent(new CustomEvent('on:cart:update', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      } finally {
        if (triggerEl) {
          triggerEl.disabled = false;
          triggerEl.classList.remove('is-loading');
        }
      }
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }
        
        // Initialize base prices for cart items when drawer opens
        // Consolidated single debounced initialization to prevent multiple overlapping calls
        // Cancel any pending initialization from previous drawer opens
        if (this.initBasePricesTimeout) {
          clearTimeout(this.initBasePricesTimeout);
        }
        
        this.initBasePricesTimeout = setTimeout(() => {
          // Check if BasePriceFetcher is available
          if (window.BasePriceFetcher) {
            // Check if cart items exist
            const cartItemsGlobal = document.querySelectorAll('.cart-item[data-product-id], tr.cart-item[data-product-id]');
            
            if (cartItemsGlobal.length > 0) {
              console.log('BasePriceFetcher: Initializing cart drawer base prices'); // eslint-disable-line
              window.BasePriceFetcher.initCartItems().catch(function(error) {
                console.error('Error initializing cart drawer base prices:', error); // eslint-disable-line
              });
            }
          } else {
            // Retry once after 500ms if script not loaded yet
            const scriptTag = document.querySelector('script[src*="base-price-fetcher.js"]');
            if (scriptTag) {
              setTimeout(() => {
                if (window.BasePriceFetcher) {
                  const cartItemsGlobal = document.querySelectorAll('.cart-item[data-product-id], tr.cart-item[data-product-id]');
                  if (cartItemsGlobal.length > 0) {
                    window.BasePriceFetcher.initCartItems().catch(function(error) {
                      console.error('Error initializing cart drawer base prices:', error); // eslint-disable-line
                    });
                  }
                }
              }, 500);
            }
          }
          this.initBasePricesTimeout = null;
        }, 200);

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const sectionsUrl = `${theme.routes.cart}?sections=${response.join(',')}`;
          const cartResponse = await fetch(sectionsUrl);
          if (!cartResponse.ok) {
            throw new Error(`Failed to refresh cart drawer sections (${cartResponse.status})`);
          }
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      const drawerContentEl = this.querySelector('.drawer__content');
      const savedDrawerContentScroll = drawerContentEl ? drawerContentEl.scrollTop : 0;
      const savedCartDrawerScroll = this.scrollTop;

      // Render sections - revert DocumentFragment for cart-drawer to preserve custom element behavior
      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          // Use direct innerHTML assignment to preserve custom element state and event listeners
          el.innerHTML = CartDrawer.getElementHTML(data.sections[section.section], section.selector);
        }
      });

      restoreCartDrawerScroll(savedDrawerContentScroll, savedCartDrawerScroll);

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }

      // Consolidate post-render operations into a single setTimeout
      // This reduces the number of timer callbacks and improves Safari performance
      setTimeout(() => {
        // Reinitialize base prices for cart items after drawer contents are rendered
        // initCartItems is already debounced, so this is safe to call
        // This will also update the savings display via updateCartSummary
        if (window.BasePriceFetcher) {
          window.BasePriceFetcher.initCartItems().catch(function(error) {
            console.error('Error initializing cart drawer base prices:', error); // eslint-disable-line
          });
        }

        // Trigger minimum order check after drawer contents are rendered
        // This ensures the check runs after the HTML is replaced with fresh content from the server
        document.dispatchEvent(new CustomEvent('cart:updated'));
        // Also try to call checkMinimumOrder directly if available
        if (window.checkMinimumOrder && typeof window.checkMinimumOrder === 'function') {
          window.checkMinimumOrder();
        }

        // Keep "Deliver all later" date input stable after section replacement.
        if (window.__isalbiDeliverAllLaterDate && typeof window.__isalbiDeliverAllLaterDate.sync === 'function') {
          window.__isalbiDeliverAllLaterDate.sync();
        }
      }, 120);
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
